import type { BaseQueryFn } from "@reduxjs/toolkit/query/react";
import { ERequestStatus } from "./async";
import {
  deleteRequest,
  getRequest,
  patchRequest,
  postRequest,
  putRequest,
} from "./fetch";
import { withRequestSignal } from "./request-signal";

export interface IAppBaseQueryArgs {
  url: string;
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

// Shared by every feature's injected endpoint module - wraps the app's
// existing fetch client (auth headers, cookies, app-specific error message
// mapping, Slack error reporting) instead of RTK Query's default
// fetchBaseQuery, so every endpoint keeps that behavior for free.
export const appBaseQuery: BaseQueryFn<
  IAppBaseQueryArgs,
  unknown,
  unknown
> = async ({ url, method = "GET", body }, { signal }) => {
  try {
    const data = await withRequestSignal(signal, () => {
      switch (method) {
        case "POST":
          return postRequest(url, body ?? {});
        case "PUT":
          return putRequest(url, body ?? {});
        case "PATCH":
          return patchRequest(url, body ?? {});
        case "DELETE":
          return deleteRequest(url);
        default:
          return getRequest(url);
      }
    });
    return { data };
  } catch (error) {
    return { error };
  }
};

// Maps an RTK Query mutation-hook result onto the app's existing
// ERequestStatus enum, so a mutation can drop into a component that used to
// read an IRequest/IPostRequest field's `.status` (e.g. a shared
// IHeaderModalAsyncStatusConfig) without that component needing to know
// whether it's now backed by RTK Query or a plain reducer.
export function statusFromMutation(result: {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
}): ERequestStatus {
  if (result.isLoading) {
    return ERequestStatus.Fetching;
  }
  if (result.isSuccess) {
    return ERequestStatus.Fetched;
  }
  if (result.isError) {
    return ERequestStatus.Error;
  }
  return ERequestStatus.None;
}

export function statusFromQuery(result: {
  isLoading: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  isError: boolean;
}): ERequestStatus {
  if (result.isLoading || result.isFetching) {
    return ERequestStatus.Fetching;
  }
  if (result.isSuccess) {
    return ERequestStatus.Fetched;
  }
  if (result.isError) {
    return ERequestStatus.Error;
  }
  return ERequestStatus.None;
}
